// src/prototype/pages/EAActivatePage.tsx
// ============================================================
// EA STEP 3: EXECUTION PORTFOLIO
// Route: /ea/activate
//
// Storefront chrome: identity header + initiative cards that open
// /ea/execute/:id. Strategic Execution Plan stays as a second tab
// because it is this branch’s live API report.
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, FileBarChart2, FileDown, LayoutGrid, Rocket } from 'lucide-react';
import { EAJourneyBar } from '../components/EAJourneyBar';
import { ExecutionPortfolioCards } from '../components/ExecutionPortfolioCards';
import { SEPReportView } from '../components/sep/SEPReportView';
import { useEA } from '../contexts/EAContext';
import { EA_CLIENT_ID } from '../constants/eaClient';
import { exportSepPdf } from '../exports/exportSepPdf';

type TabId = 'portfolio' | 'sep';

export default function EAActivatePage() {
  const navigate = useNavigate();
  const { canAccessProSteps, finalistIds, finalistOrder } = useEA();
  const clientId = EA_CLIENT_ID;
  const [activeTab, setActiveTab] = useState<TabId>('portfolio');
  const [isExporting, setIsExporting] = useState(false);

  const portfolioIds = useMemo(() => Array.from(finalistIds), [finalistIds]);

  useEffect(() => {
    if (!canAccessProSteps) navigate('/ea/upgrade');
  }, [canAccessProSteps, navigate]);

  const handleExportSep = useCallback(async () => {
    if (!document.getElementById('sep-report-export-root')) {
      window.alert('The Strategic Execution Plan report is not ready to export yet.');
      return;
    }
    setIsExporting(true);
    try {
      await exportSepPdf({ elementId: 'sep-report-export-root', clientIdentifier: clientId });
    } catch (err) {
      console.error('[EAActivatePage] SEP PDF export failed:', err);
      const message = err instanceof Error ? err.message : String(err);
      window.alert(`PDF export failed: ${message}`);
    } finally {
      setIsExporting(false);
    }
  }, [clientId]);

  const tabs = [
    { id: 'portfolio' as const, label: 'Initiative Portfolio', icon: <LayoutGrid className="w-3.5 h-3.5" /> },
    { id: 'sep' as const, label: 'Strategic Execution Plan', icon: <FileBarChart2 className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="px-6 pt-3 pb-2 print:hidden">
        <EAJourneyBar />
      </div>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
          <div className="p-6 pb-5">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <span className="text-[10px] font-semibold text-[#FF6E4C] uppercase tracking-wide">Execute</span>
                <h1 className="text-2xl font-bold text-[#18365E] leading-tight mt-1">Execution Portfolio</h1>
                <p className="text-sm text-[#717182] mt-1 max-w-2xl">
                  {portfolioIds.length} approved initiative{portfolioIds.length !== 1 ? 's' : ''} · champion and initiate each through
                  approval, activation, and delivery
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/ea/opportunities')}
                className="text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-3 py-2 hover:border-[#18365E] flex items-center gap-1.5 transition-colors bg-white shrink-0"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to Evaluate
              </button>
            </div>
          </div>
        </div>

        {portfolioIds.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#e5e7eb] px-6 py-14 text-center">
            <Rocket className="w-10 h-10 text-[#e5e7eb] mx-auto mb-3" />
            <p className="text-sm font-bold text-[#18365E]">No initiatives yet</p>
            <p className="text-xs text-[#717182] mt-1 max-w-md mx-auto leading-relaxed">
              Initiatives enter this portfolio when you proceed with them in Evaluate. Open a decision brief and take the
              action its verdict recommends.
            </p>
            <button
              type="button"
              onClick={() => navigate('/ea/opportunities')}
              className="mt-5 bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 hover:bg-[#e85f3f] transition-colors"
            >
              Go to Evaluate
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1 p-1 bg-[#18365E]/[0.06] rounded-xl w-fit">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                        isActive
                          ? 'bg-[#18365E] text-white shadow-sm'
                          : 'text-[#18365E]/70 hover:text-[#18365E] hover:bg-white/60'
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {activeTab === 'sep' && (
                <button
                  type="button"
                  onClick={handleExportSep}
                  disabled={isExporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[12px] font-semibold text-[#18365E] bg-white hover:bg-[#F5F7FA] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isExporting ? (
                    <>
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-[#18365E]/30 border-t-[#18365E] animate-spin" />
                      Exporting…
                    </>
                  ) : (
                    <>
                      <FileDown className="w-3.5 h-3.5" />
                      Export
                    </>
                  )}
                </button>
              )}
            </div>

            {activeTab === 'portfolio' && (
              <ExecutionPortfolioCards
                clientId={clientId}
                portfolioIds={portfolioIds}
                finalistOrder={finalistOrder}
                onOpen={(id) => navigate(`/ea/execute/${id}`)}
              />
            )}

            {activeTab === 'sep' && (
              <SEPReportView clientId={clientId} portfolioIds={portfolioIds} />
            )}
          </>
        )}

        <p className="text-[11px] text-[#717182]">
          Initiatives enter this portfolio when you proceed with them in <b>Evaluate</b>. The evaluation brief stays
          linked from every command center.
        </p>
      </main>
    </div>
  );
}
